//Types
import { Nullable } from "@/Common/interfaces/types";
import { ClassNameHints, Issue } from "@/Common/types/tables";

// Constants
import { FOOTER_PARAM_ID, GROUP_NODE_ROOT_ID, TABLE_TOTAL_MARKER } from "@/Constants/tables";
import { isSubTotalCell } from "@/Washouts/helpers/tables";

//MUI
import { GridCellParams, GridValidRowModel } from "@mui/x-data-grid-premium";

//Libraries
import clsx from "clsx";

export function isFooterCell(params: Nullable<GridCellParams<GridValidRowModel>>): boolean {
	return params?.id === FOOTER_PARAM_ID;
}

export function isTreeNodeChildCell(params: Nullable<GridCellParams<GridValidRowModel>>): boolean {
	return params?.rowNode.parent !== GROUP_NODE_ROOT_ID;
}

export function getCellClassNameFn(
	issues: Issue[] = [],
	hintsOverride: Partial<ClassNameHints> = {},
	classes: {
		footer?: string;
		base?: string;
	} = { footer: "", base: "" },
	reference = ""
) {
	return (params: GridCellParams) => {
		const hints = { ...computeProps(reference, params, issues), ...hintsOverride };
		return clsx({
			"!border-none": true,
			[`${classes.base ?? ""}`]: true,
			[`${classes.footer ?? ""}`]: hints.isFooter,
			"!bg-[var(--primary-color)] !text-white": !hints.isNegative && hints.isFooter,
			"!bg-[var(--light-gray-color-v2)] !text-[var(--text-alert-color)]": hints.isNegative && hints.isFooter,
			"!overflow-visible !relative": hints.noWrap && hints.isFooter,
			[String.raw`[&[data-field='\_\_tree\_data\_group\_\_']]:!bg-[#FFF]`]: isTreeNodeChildCell(params),
			"no-focus": hints.noFocus,
			"table-noWrap": hints.noWrap,
			"simple-negative-value": hints.isNegative,
			[`cell-color-${hints.colorIssued?.replace("#", "")}`]: hints.colorIssued,
		});
	};
}

export function computeProps(
	reference: Nullable<string>,
	params: Nullable<GridCellParams<GridValidRowModel>>,
	issues: Issue[] = []
): ClassNameHints {
	const colorIssued = isCellIssued(params?.row._id, params?.field, reference, issues);
	const isTotal = Boolean(params?.id?.toString().includes(TABLE_TOTAL_MARKER));
	const isFooter = isFooterCell(params);
	const isSubTotalRow = isSubTotalCell(params?.row);
	const isNumber = params?.colDef.type === "number" || typeof params?.value === "number";
	return {
		colorIssued,
		isTotal,
		isFooter,
		isSubTotalRow,
		isNegative: isNumber && Math.sign(Number(params?.value)) === -1,
		isFunded: (params?.row?.funded || false) && !isTotal,
		noFocus: isFooter || !colorIssued,
		isMoney: isNumber && (isMoneyRelated(params?.field) || isMoneyRelated(params?.colDef.headerName ?? "")),
		noWrap: false,
		isPercentage: false,
	};
}

export function isCellIssued(
	_id: string,
	field: Nullable<string>,
	reference: Nullable<string>,
	issues: Array<Issue> = []
): Nullable<string> {
	if (issues?.length) {
		for (const issue of issues) {
			if (issue && issue.tableReference === reference && issue.rowReference === _id && issue.columnReference === field) {
				return issue.color;
			}
		}
	}
	return null;
}

const MONEY_KEY_WORDS = [
	"gross",
	"commission",
	"profit",
	"compensation",
	"pvr",
	"invoice",
	"warranty",
	"finance reserve",
	"gap/aftmkt/maint",
	"uwcer",
	"fee",
];

export function isMoneyRelated(field: string): boolean {
	return MONEY_KEY_WORDS.some((word) => field.toLowerCase().includes(word));
}

export function isEmptyRow(params?: GridCellParams): boolean {
	return Boolean(params?.row?.emptyRow) || String(params?.id).startsWith("self-generated-empty-row");
}

export function getEmptyColumnDefinition(width = 24) {
	return {
		field: "",
		headerName: "",
		sortable: false,
		minWidth: width,
		maxWidth: width,
		disableReorder: true,
		headerClassName: "z-10 !bg-white",
		cellClassName: "!bg-white shadow-[0_2px_0_0_white]",
	};
}
